/**
 * Uploading one file in chunks.
 *
 * The server keeps the authoritative offset and refuses a chunk that does not
 * match it, answering 409 with where it actually is. That is what makes a
 * resume trivial: on a conflict, ask, and carry on from there rather than
 * starting the file again.
 */

import { api, ApiError, type Upload } from '../api/client'

/** A dropped connection is worth a couple of retries before giving up on the
 *  whole file; a batch of twenty should not die because one chunk blipped. */
const MAX_CHUNK_ATTEMPTS = 3

export class UploadAborted extends Error {
  constructor() {
    super('Upload cancelled.')
    this.name = 'UploadAborted'
  }
}

export interface UploadHandle {
  /** Set once the server has created the session, so a cancel can clean up. */
  uploadId?: string
}

export async function uploadFile(
  file: File,
  {
    onProgress,
    signal,
    handle,
  }: {
    onProgress?: (fraction: number) => void
    signal?: AbortSignal
    handle?: UploadHandle
  } = {},
): Promise<Upload> {
  const throwIfCancelled = () => {
    if (signal?.aborted) throw new UploadAborted()
  }

  throwIfCancelled()
  const session = await api.startUpload(file.name, file.size)
  if (handle) handle.uploadId = session.id

  let offset = session.received_bytes
  const chunkSize = session.chunk_size

  while (offset < file.size) {
    throwIfCancelled()
    const end = Math.min(offset + chunkSize, file.size)
    let attempt = 0

    for (;;) {
      attempt += 1
      try {
        const progress = await api.uploadChunk(session.id, offset, file.slice(offset, end))
        offset = progress.received_bytes
        break
      } catch (cause) {
        throwIfCancelled()
        if (cause instanceof ApiError && cause.status === 409) {
          // Someone else's chunk landed first, or a previous attempt actually
          // succeeded and we never saw the answer. Ask where the server is.
          const current = await api.getUpload(session.id)
          offset = current.received_bytes
          break
        }
        // A transport failure may or may not have been applied; the offset
        // rule makes retrying safe either way.
        const transient = cause instanceof ApiError && (cause.status === 0 || cause.status >= 500)
        if (!transient || attempt >= MAX_CHUNK_ATTEMPTS) throw cause
        await new Promise((resolve) => setTimeout(resolve, 250 * attempt))
      }
    }

    onProgress?.(Math.min(1, offset / file.size))
  }

  throwIfCancelled()
  return api.completeUpload(session.id)
}
